/**
 * worldArt.ts — ONE implementation of world-object drawing (the dungeon
 * look pass). The game Renderer (game + arena), the sprite lab stage, and
 * the rooms-editor preview all draw through this module — same atlas lookups,
 * same anchor math, same fallback rules, so the three surfaces can't drift
 * apart (the structural fix for lab/arena/game inconsistencies).
 *
 * Pure canvas over the committed atlas (tools/sprites substrate: floor tiles
 * + the `props` section). No sim knowledge, no decisions — every call takes
 * explicit screen coords + the iso unit scale `u`. The projection contract
 * is iso.ts (2:1 dimetric, 96×48 tiles): a tile's top corner is at
 * (sx0, sy0); its diamond centre (the ground point for things standing on
 * the tile) is at (sx0, sy0 + HALF_H·u).
 *
 * Anchor math: a prop frame's atlas `anchor` is the GROUND POINT within the
 * frame (committed atlas dims — verify against atlas.json, they have
 * outlived this comment before): wall 96×120, anchor [48,96] (a block whose
 * top face sits 1½ tiles up — its silhouette is what gen's key-visibility
 * exclusion is calibrated to); pond 96×48, [48,24] (flat, the tile centre);
 * table 96×72, [48,40] (the half-tile block's footprint centre); door 96×148,
 * [48,124] (the tall closed/open structure). Drawing the frame so its anchor
 * lands on the tile diamond centre places the object's footprint exactly on
 * the tile.
 *
 * Fallback rules (missing atlas = the same honesty as the player): floor
 * tiles fall back to the flat diamond (the M0–M2 playability contract);
 * props/doors draw NOTHING (a missing prop must never render as a
 * placeholder shape — the letter/card fallbacks are gone).
 */
import { TILE_H, TILE_W, HALF_H, HALF_W } from "./iso.js";
import type { Atlas } from "./atlas.js";
import { MARKER_CONFIG, outlineSprite } from "./markers.js";
import { spriteStore } from "./spriteStore.js";
import type { OutlineStyle } from "./markers.js";

/** The flat-diamond floor fallback (playability without sprites). */
export const FLOOR_FALLBACK = "#222228";

/** The prop vocabulary (the substrate's `props` atlas section). */
export const PROP_WALL = "wall";
export const PROP_POND = "pond";
export const PROP_TABLE = "table";
export const PROP_DOOR_CLOSED = "door-closed";
export const PROP_DOOR_OPEN = "door-open";

/** State.terrain id -> prop name (0 = bare floor, no prop). */
const TERRAIN_PROP: Record<number, string> = {
  1: PROP_WALL,
  2: PROP_POND,
  3: PROP_TABLE,
};

/** Stable, coordinate-only cosmetic randomisation. It never sees the map
 *  seed or mutates state, so replays and fog guarantees stay untouched.
 *  Exported because the sign-in scene draws the town's floor and must wear
 *  it EXACTLY as the Viewport does — a second approximation would show the
 *  same square wearing two different floors. */
export function tileHash(x: number, y: number): number {
  let h = Math.imul(x ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(y ^ 0xc2b2ae35, 0x27d4eb2d);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Frame zero is the clean base; the other civic frames are rare repair,
 *  crack, and damp accents. Wear controls rate, never tile semantics.
 *  `architecture` is a string (not the shared union) because the Renderer
 *  holds it as one — the rooms catalog feeds it through untyped. */
export function floorFrame(architecture: string, wear: number, x: number, y: number): number {
  if (architecture !== "civic-stone-v1") return 0;
  const r = tileHash(x, y) % 100;
  if (r >= wear) return 0;
  return 1 + (tileHash(y, x) % 3);
}

export class WorldArt {
  /** Frame image cache (path -> Image), shared by every draw call. An
   *  errored image stays cached (complete, naturalWidth 0) — it is
   *  permanently absent, never retried, never waited on. */
  private imgCache = new Map<string, HTMLImageElement>();
  /** Frames that were NEEDED but still decoding during the current draw
   *  pass (beginPass/drainMissed). One-shot surfaces use this to re-render
   *  when the frames arrive; per-frame surfaces (game/arena/lab) never call
   *  it and the set is simply never drained (harmless). */
  private missed = new Set<string>();

  /**
   * @param atlas the parsed atlas (the `floor-<palette>` tiles + `props`).
   * @param base  the sprite root URL — "/sprites" for the committed tree
   *  (game/arena/editor); the lab's compare mode re-points it at the
   *  pre-regen snapshot ("/sprites/prev").
   */
  constructor(private atlas: Atlas, private base: string = "/sprites") {}

  /** A cached frame image, or null until it decodes (first frames). */
  img(path: string): HTMLImageElement | null {
    const packed = this.base === "/sprites" ? spriteStore() : null;
    if (packed?.has(path)) {
      const im = packed.image(path);
      return im.complete && im.naturalWidth > 0 ? im : null;
    }
    let im = this.imgCache.get(path);
    if (!im) {
      im = new Image();
      im.src = `${this.base}/${path}?v=${this.atlas.version}`;
      this.imgCache.set(path, im);
    }
    return im.complete && im.naturalWidth > 0 ? im : null;
  }

  /** The floor tile image for a palettes.json palette (the room's tile
   *  shade). Null when the atlas has no `floor-<palette>` entry yet. */
  floorTile(palette: string, frame = 0): HTMLImageElement | null {
    const s = this.atlas.sprites[`floor-${palette}`];
    const p = s && s.tile ? s.frames[frame] ?? s.frames[0] : undefined;
    return p ? this.needImg(p) : null;
  }

  /** The first frame of a named prop, or null (missing section / undecoded). */
  propImg(name: string): HTMLImageElement | null {
    const p = this.atlas.props?.[name]?.frames[0];
    return p ? this.needImg(p) : null;
  }
  /** Start a draw pass (clears the missed-frame set). */
  beginPass(): void {
    this.missed.clear();
  }
  /** The frames that were needed-but-still-decoding during the last pass
   *  (drains the set). A one-shot surface (the rooms-editor preview)
   *  re-renders once these arrive — a pending-check run AFTER the draw
   *  loop would miss any decode that lands in the gap between the two and
   *  never re-render. */
  drainMissed(): string[] {
    const out = [...this.missed];
    this.missed.clear();
    return out;
  }
  /** Resolve once every frame in `paths` has settled (decoded, or errored
   *  — an errored frame stays honestly absent). */
  ensureLoaded(paths: string[]): Promise<void> {
    return Promise.all(paths.map(
      (p) => new Promise<void>((res) => {
        const im = this.imgCache.get(p);
        if (!im || im.complete) {
          res();
          return;
        }
        im.addEventListener("load", () => res(), { once: true });
        im.addEventListener("error", () => res(), { once: true });
      }),
    )).then(() => undefined);
  }
  /**
   * A frame image, recording the MISS when it is still decoding (so a
   *  one-shot surface's pass can wait for it). An errored image (complete,
   *  zero width) is absent forever and is NOT recorded — waiting on it
   *  would hang, and re-rendering would loop.
   */
  private needImg(path: string): HTMLImageElement | null {
    const im = this.img(path);
    if (im) return im;
    const cached = this.imgCache.get(path) ?? (this.base === "/sprites" ? spriteStore()?.image(path) : undefined);
    if (cached && cached.complete) return null; // errored — never again
    this.missed.add(path);
    return null;
  }
  /** Internal: a prop's frame image + dims/anchor (null when missing). */
  private propFrame(name: string, frame = 0): { im: HTMLImageElement; def: NonNullable<Atlas["props"]>[string] } | null {
    const def = this.atlas.props?.[name];
    const p = def?.frames[frame] ?? def?.frames[0];
    if (!def || !p) return null;
    const im = this.needImg(p);
    return im ? { im, def } : null;
  }

  /**
   * The floor tile diamond with its top corner at (sx0, sy0). Real art when
   * the image is available, else the flat fallback diamond.
   */
  drawFloorTile(ctx: CanvasRenderingContext2D, sx0: number, sy0: number, u: number, img: HTMLImageElement | null): void {
    if (img) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, sx0 - HALF_W * u, sy0, TILE_W * u, TILE_H * u);
      return;
    }
    ctx.fillStyle = FLOOR_FALLBACK;
    ctx.beginPath();
    ctx.moveTo(sx0, sy0);
    ctx.lineTo(sx0 + HALF_W * u, sy0 + HALF_H * u);
    ctx.lineTo(sx0, sy0 + TILE_H * u);
    ctx.lineTo(sx0 - HALF_W * u, sy0 + HALF_H * u);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * A prop frame anchored so its atlas ground point lands on (cx, cy) —
   * pass the tile diamond centre (sx0, sy0 + HALF_H·u) for objects on tile
   * (x, y). Returns true when the frame drew (false = missing atlas → the
   * caller draws nothing, exactly like a missing player).
   */
  drawAnchored(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, name: string, frame = 0): boolean {
    const f = this.propFrame(name, frame);
    if (!f) return false;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(f.im, cx - f.def.anchor[0] * u, cy - f.def.anchor[1] * u, f.def.dims[0] * u, f.def.dims[1] * u);
    return true;
  }

  /**
   * Terrain for one revealed tile (the State.terrain id, 0 = bare floor):
   * the floor diamond always first (it is the ground the prop sits on),
   * then the obstacle prop. Returns true when a prop drew.
   */
  drawTerrain(ctx: CanvasRenderingContext2D, sx0: number, sy0: number, u: number, terrain: number, floorImg: HTMLImageElement | null, propFrame = 0): boolean {
    this.drawFloorTile(ctx, sx0, sy0, u, floorImg);
    const prop = TERRAIN_PROP[terrain];
    if (!prop) return false;
    return this.drawAnchored(ctx, sx0, sy0 + HALF_H * u, u, prop, propFrame);
  }

  /**
   * A door's state art at the door tile's top corner (sx0, sy0). Closed =
   * the full stone block with its slab; open = the threshold (the dark
   * passage reads the gap). Axis-agnostic: the map geometry, not the art,
   * says which way the door faces. Returns true when the frame drew.
   */
  drawDoor(ctx: CanvasRenderingContext2D, sx0: number, sy0: number, u: number, open: boolean): boolean {
    return this.drawAnchored(ctx, sx0, sy0 + HALF_H * u, u, open ? PROP_DOOR_OPEN : PROP_DOOR_CLOSED);
  }

  /**
   * The unified contact shadow: a soft dark ellipse on the ground plane
   * (the hero, the enemies, the dropped items — one helper, never baked
   * into a PNG). (cx, cy) = the feet point; rx/ry in css px.
   */
  drawContactShadow(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, rx: number, ry: number, alpha = 0.38): void {
    ctx.fillStyle = `rgba(10, 8, 6, ${alpha})`;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx * u, ry * u, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * The interact outline for a door: the silhouette of the ACTUAL art
   * (halo + colour passes, the item-outline pattern) instead of a
   * procedural slab rect — the outline always matches the pixels.
   */
  outlineDoor(ctx: CanvasRenderingContext2D, sx0: number, sy0: number, u: number, open: boolean, style: OutlineStyle): void {
    const f = this.propFrame(open ? PROP_DOOR_OPEN : PROP_DOOR_CLOSED);
    if (!f) return;
    const cfg = MARKER_CONFIG;
    const dil = style.dilation * u;
    const halo = style.haloWidth * u;
    const destX = sx0 - f.def.anchor[0] * u;
    const destY = sy0 + HALF_H * u - f.def.anchor[1] * u;
    const destW = f.def.dims[0] * u;
    const destH = f.def.dims[1] * u;
    outlineSprite(ctx, { image: f.im }, destX, destY, destW, destH, dil + halo, cfg.haloColor, style.alpha * style.haloAlpha);
    outlineSprite(ctx, { image: f.im }, destX, destY, destW, destH, dil, cfg.interactColor, style.alpha);
  }

  /**
   * The interact outline for a terrain prop (wall/pond/table) — the rooms
   * editor and any host that outlines furniture use the same silhouette
   * pass. (cx, cy) = the tile diamond centre.
   */
  outlineProp(ctx: CanvasRenderingContext2D, cx: number, cy: number, u: number, name: string, style: OutlineStyle): void {
    const f = this.propFrame(name);
    if (!f) return;
    const cfg = MARKER_CONFIG;
    const dil = style.dilation * u;
    const halo = style.haloWidth * u;
    const destX = cx - f.def.anchor[0] * u;
    const destY = cy - f.def.anchor[1] * u;
    const destW = f.def.dims[0] * u;
    const destH = f.def.dims[1] * u;
    outlineSprite(ctx, { image: f.im }, destX, destY, destW, destH, dil + halo, cfg.haloColor, style.alpha * style.haloAlpha);
    outlineSprite(ctx, { image: f.im }, destX, destY, destW, destH, dil, cfg.interactColor, style.alpha);
  }
}
